import { api } from './index';
import { TenderItem } from './tenderItems';
import { EvaluationCriteria } from './evaluationCriteria';

// Types
export interface Tender {
  id: string;
  referenceNo: string;
  description: string;
  category: 'GOODS' | 'SERVICES' | 'WORKS';
  publishingDate: string;
  submissionDeadline: string;
  openingDate?: string;
  stage: 'DRAFT' | 'PENDING_APPROVAL' | 'PUBLISHED' | 'COLLECTION' | 'OPENED' | 'TECH_EVAL' | 'FIN_EVAL' | 'AWARDED' | 'EXPIRED';
  awardedVendor?: string;
  dashboardCache?: any;
  // TOR fields
  torSpecification?: string | null;
  torDocumentId?: string | null;
  // Flags
  technicalEvaluationRequired?: boolean;
  technicalEvaluatorEmails?: string[];
  requirePrebidMeeting?: boolean;
  prebidMeetingDate?: string;
  prebidMeetingVenue?: string;
  requireRegistration?: boolean;
  currency?: 'MVR' | 'USD';
  // Draft and approval fields
  draftStatus: 'SAVED' | 'SUBMITTED';
  lastSavedAt: string;
  savedById?: string;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  approvedById?: string;
  approvedAt?: string;
  approvalComments?: string;
  bidders: Bidder[];
  documents: TenderDocument[];
  items?: TenderItem[];
  evaluationCriteria?: EvaluationCriteria[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface Bidder {
  id: string;
  tenderId: string;
  name: string;
  bidPrice: number;
  techScore?: number;
  finScore?: number;
  ranking?: number;
  isAwarded: boolean;
}

export interface TenderDocument {
  id: string;
  tenderId: string;
  type: 'ANNOUNCEMENT' | 'TOR' | 'COLLECTION_SHEET' | 'TECH_EVAL_SHEET' | 'FIN_EVAL_SHEET' | 'BID_REGISTER' | 'AWARD_DECISION' | 'OTHER';
  bucket: string;
  objectKey: string;
  fileName: string;
  uploadedById?: string;
  uploadedAt: string;
}

export interface CreateTenderDto {
  referenceNo?: string;
  description: string;
  category: 'GOODS' | 'SERVICES' | 'WORKS';
  publishingDate: string;
  submissionDeadline: string;
  openingDate?: string;
  torSpecification?: string;
  torDocumentId?: string;
  technicalEvaluationRequired?: boolean;
  technicalEvaluatorEmails?: string[];
  requirePrebidMeeting?: boolean;
  prebidMeetingDate?: string;
  prebidMeetingVenue?: string;
  requireRegistration?: boolean;
  currency?: 'MVR' | 'USD';
  documentRequirements?: {
    documentType: string;
    documentCategory?: 'FINANCIAL' | 'GENERAL' | 'TECHNICAL';
    description?: string;
    isRequired: boolean;
    order: number;
  }[];
  items?: {
    itemName: string;
    description?: string;
    quantity: number;
    unit: string;
    order: number;
  }[];
  evaluationCriteria?: {
    criteriaName: string;
    description?: string;
    criteriaType?: 'FINANCIAL' | 'GENERAL';
    weightage: number;
    order: number;
    isDefault: boolean;
  }[];
}

export type UpdateTenderDto = Partial<CreateTenderDto>;

export interface UpdateStageDto {
  stage: 'DRAFT' | 'PENDING_APPROVAL' | 'PUBLISHED' | 'COLLECTION' | 'OPENED' | 'TECH_EVAL' | 'FIN_EVAL' | 'AWARDED' | 'EXPIRED';
}

export interface BidderDto {
  name: string;
  bidPrice: number;
}

export interface ScoreDto {
  bidderId: string;
  score: number;
}

export interface DocUploadDto {
  type: 'ANNOUNCEMENT' | 'TOR' | 'COLLECTION_SHEET' | 'TECH_EVAL_SHEET' | 'FIN_EVAL_SHEET' | 'BID_REGISTER' | 'AWARD_DECISION' | 'OTHER';
  fileName: string;
  base64File: string;
}

export interface AwardVendorDto {
  bidderId: string;
  remarks?: string;
}

export interface TenderFilterDto {
  stage?: 'DRAFT' | 'PENDING_APPROVAL' | 'PUBLISHED' | 'COLLECTION' | 'OPENED' | 'TECH_EVAL' | 'FIN_EVAL' | 'AWARDED' | 'EXPIRED';
  category?: 'GOODS' | 'SERVICES' | 'WORKS';
  search?: string;
}

export interface TenderDashboard {
  totalTenders: number;
  publishedTenders: number;
  activeTenders: number;
  awardedTenders: number;
  expiringTenders: number;
  avgTurnaround: number;
}

// API functions
export const tenderApi = {
  // List tenders
  listTenders: async (filter?: TenderFilterDto): Promise<Tender[]> => {
    const params = new URLSearchParams();
    if (filter?.stage) params.append('stage', filter.stage);
    if (filter?.category) params.append('category', filter.category);
    if (filter?.search) params.append('search', filter.search);
    
    const response = await api.get(`/tenders?${params.toString()}`);
    return response.data;
  },

  // Get tender by ID
  getTenderById: async (id: string): Promise<Tender> => {
    const response = await api.get(`/tenders/${id}`);
    return response.data;
  },

  // Create tender
  createTender: async (data: CreateTenderDto): Promise<Tender> => {
    const response = await api.post('/tenders', data);
    return response.data;
  },

  // Update tender
  updateTender: async (id: string, data: UpdateTenderDto): Promise<Tender> => {
    const response = await api.patch(`/tenders/${id}`, data);
    return response.data;
  },

  // Update tender stage
  updateStage: async (id: string, data: UpdateStageDto): Promise<Tender> => {
    const response = await api.patch(`/tenders/${id}/stage`, data);
    return response.data;
  },

  // Add bidder
  addBidder: async (tenderId: string, data: BidderDto): Promise<Bidder> => {
    const response = await api.post(`/tenders/${tenderId}/bidders`, data);
    return response.data;
  },

  // Upload document
  uploadDoc: async (tenderId: string, data: DocUploadDto): Promise<TenderDocument> => {
    const response = await api.post(`/tenders/${tenderId}/docs`, data);
    return response.data;
  },

  // Record technical scores
  recordTechScores: async (tenderId: string, scores: ScoreDto[]): Promise<Tender> => {
    const response = await api.post(`/tenders/${tenderId}/tech-scores`, { scores });
    return response.data;
  },

  // Record financial scores
  recordFinScores: async (tenderId: string, scores: ScoreDto[]): Promise<Tender> => {
    const response = await api.post(`/tenders/${tenderId}/fin-scores`, { scores });
    return response.data;
  },

  // Award vendor
  awardVendor: async (tenderId: string, data: AwardVendorDto): Promise<Tender> => {
    const response = await api.post(`/tenders/${tenderId}/award`, data);
    return response.data;
  },

  // Get tender dashboard
  getTenderDashboard: async (): Promise<TenderDashboard> => {
    const response = await api.get('/tenders/dashboard');
    return response.data;
  },

  // Get expiring tenders
  getExpiringTenders: async (): Promise<Tender[]> => {
    const response = await api.get('/tenders/expiring');
    return response.data;
  },

  // Get pending approvals
  getPendingApprovals: async (): Promise<Tender[]> => {
    const response = await api.get('/tenders/pending-approvals');
    return response.data;
  },
}; 